import { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { Link, useLoaderData, useRouteLoaderData } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import AutoplayCarousel from "~/components/ui/AutoplayCarousel";
import { Button } from "~/components/ui/Button";
import {
  CarouselContent,
  CarouselDots,
  CarouselItem,
} from "~/components/ui/Carousel";
import { getLocale } from "~/lib/i18n.server";
import { createSitemapFunction } from "~/lib/sitemap-generator";
import { getMenuHref, getMetaData } from "~/lib/utils";

export const sitemap = createSitemapFunction("food");

export const meta: MetaFunction = (data: any) => {
  const { t } = useTranslation();
  return getMetaData(data, {
    title: t("Sapori"),
    description: t(
      "Scopri il nostro bistrot: un'esperienza culinaria che unisce gusto e benessere. Piatti sani e gustosi, preparati con ingredienti freschi e di stagione, per completare il tuo percorso di relax."
    ),
  });
};

export async function loader({ context, request }: LoaderFunctionArgs) {
  try {
    const pageSlug = "food";

    const locale = await getLocale(request);
    const lang = locale;
    const res = await fetch(
      `${context.cloudflare.env.WP_API_HOST}/wp-json/wp/v2/pages?slug=${pageSlug}&lang=${lang}`
    );
    const pages: any = await res.json();
    if (!res.ok) {
      throw new Error(`Failed to fetch: ${res.statusText}`);
    }
    if (pages && pages.length > 0) {
      return {
        page: pages[0],
        seo: pages[0]?.yoast_head_json,
        lang,
      };
    } else {
      return {
        error: "No pages found for the given slug.",
        page: {},
      };
    }
  } catch (err) {
    return {
      err: err,
      page: {},
    };
  }
}

export default function Food() {
  const loaderData = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation();
  const { bookingDisabled, shopDisabled } = useRouteLoaderData<any>("root");
  return (
    <div
      className={`
        relative
      `}
    >
      <main className="flex flex-col lg:pb-12">
        {loaderData.page.acf?.hero && (
          <section
            className="bg-cover bg-center bg-no-repeat "
            style={{
              backgroundImage: `url('/cms-asset/${loaderData.page.acf?.hero.image.id}')`,
            }}
          >
            <div className="bg-black bg-opacity-40 py-32">
              <div className="flex flex-col max-w-4xl mx-auto items-center justify-center">
                <h1 className="text-4xl lg:text-5xl text-white font-tenor-sans uppercase">
                  {loaderData.page.acf?.hero.title}
                </h1>
                <hr className="block w-16 h-[2px] bg-white mt-2" />
                <p className="text-white text-lg mt-4">
                  {loaderData.page.acf?.hero.description}
                </p>
              </div>
            </div>
          </section>
        )}

        {loaderData.page && loaderData.page.acf?.intro_section && (
          <section className="w-full">
            <div className="flex flex-col items-center max-w-7xl mx-auto py-12 px-4">
              <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase max-w-xl">
                {loaderData.page.acf?.intro_section?.title}
              </h4>
              <hr className="h-0.5 bg-secondary w-44 mt-4" />
              <h5 className="text-secondary mt-4">
                {loaderData.page.acf?.intro_section?.subtitle}
              </h5>
              <img
                alt=""
                src={`/cms-asset/${loaderData.page.acf?.intro_section?.banner_image?.id}`}
                className="w-full h-32 object-contain object-center"
              />
              <div
                className="mt-4 h5-mb-8 text-center"
                dangerouslySetInnerHTML={{
                  __html: loaderData.page.acf?.intro_section?.description,
                }}
              />
            </div>
          </section>
        )}

        {loaderData.page && loaderData.page.acf?.bistrot_section && (
          <section className="flex flex-col items-center justify-center pb-20 max-w-7xl mx-auto px-4">
            {/* <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
              {loaderData.page.acf?.bistrot_section.title}
            </h4>
            <hr className="block w-40 h-[2px] bg-secondary mt-4" />
            <div
              className="text-center mt-4"
              dangerouslySetInnerHTML={{
                __html: loaderData.page.acf?.bistrot_section.description,
              }}
            /> */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
              {loaderData.page.acf?.bistrot_section.cards.map(
                (card: any, index: number) => (
                  <div key={index} className="flex flex-col bg-muted">
                    <img
                      alt=""
                      src={`/cms-asset/${card.image.id}`}
                      className="w-full h-80 object-cover object-center"
                    />
                    <div className="p-4">
                      <h4 className="text-secondary text-3xl uppercase font-tenor-sans mt-6">
                        {card.title}
                      </h4>
                      <hr className="block w-12 h-[2px] bg-secondary mt-2" />
                      <h5 className="font-bold mt-2 text-black">
                        {card.subtitle}
                      </h5>
                      <div
                        className="text-sm"
                        dangerouslySetInnerHTML={{ __html: card.description }}
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          </section>
        )}

        {/* {loaderData.page && loaderData.page.acf?.intro_section_2 && (
          <section className="w-full bg-muted">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
              <div>
                <img
                  alt=""
                  src={`/cms-asset/${loaderData.page.acf?.intro_section_2?.image?.id}`}
                  className="w-full h-96 object-contain object-center"
                />
              </div>
              <div className="flex flex-col">
                <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase max-w-xl">
                  {loaderData.page.acf?.intro_section_2?.title}
                </h4>
                <hr className="h-0.5 bg-secondary w-44 mt-4" />
                <div
                  className="mt-4 h5-mb-8"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.intro_section_2?.description,
                  }}
                />
                <div className="grid grid-cols-1 gap-4 mt-4">
                  {loaderData.page.acf?.intro_section_2?.rows?.map(
                    (row: any, index: number) => {
                      return (
                        <div className="flex flex-col" key={index}>
                          <h5 className="font-bold mb-1">{row.title}</h5>
                          <p className="">{row.description}</p>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          </section>
        )} */}

        {loaderData.page && loaderData.page.acf?.info_section && (
          <section className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
              <div className="flex flex-col">
                <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                  {loaderData.page.acf?.info_section.title}
                </h4>
                <hr className="h-0.5 bg-secondary w-44 mt-4" />
                <div
                  className="mt-4"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.info_section.description,
                  }}
                />
              </div>
              <div>
                <AutoplayCarousel className="w-full">
                  <CarouselContent>
                    {loaderData.page.acf?.info_section?.images?.map(
                      (_: any, index: number) => (
                        <CarouselItem key={index}>
                          <div className="w-full h-96">
                            <span className="text-4xl font-semibold">
                              <img
                                alt=""
                                src={`/cms-asset/${_.image}`}
                                className="w-full h-full object-cover object-center"
                              />
                            </span>
                          </div>
                        </CarouselItem>
                      )
                    )}
                  </CarouselContent>
                  <CarouselDots className="relative top-0 bottom-0 mt-4" />
                </AutoplayCarousel>
              </div>
            </div>
          </section>
        )}

        {loaderData.page && loaderData.page.acf?.info_section_2 && (
          <section className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
              <div className="order-2 lg:order-1">
                <AutoplayCarousel className="w-full">
                  <CarouselContent>
                    {loaderData.page.acf?.info_section_2.images?.map(
                      (_: any, index: number) => (
                        <CarouselItem key={index}>
                          <div className="w-full h-96">
                            <span className="text-2xl lg:text-4xl font-semibold">
                              <img
                                alt=""
                                src={`/cms-asset/${_.image.id}`}
                                className="w-full h-full object-cover object-center"
                              />
                            </span>
                          </div>
                        </CarouselItem>
                      )
                    )}
                  </CarouselContent>
                  <CarouselDots className="relative top-0 bottom-0 mt-4" />
                </AutoplayCarousel>
              </div>
              <div className="flex flex-col order-1 lg:order-2">
                <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                  {loaderData.page.acf?.info_section_2.title}
                </h4>
                <hr className="h-0.5 bg-secondary w-44 mt-4" />
                <div
                  className="mt-4"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.info_section_2.description,
                  }}
                />
              </div>
            </div>
          </section>
        )}

        {loaderData.page.acf?.book_section && (
          <section className="max-w-7xl mx-auto w-full">
            <div
              className="bg-cover bg-center bg-no-repeat w-full"
              style={{
                backgroundImage: `url('/cms-asset/${loaderData.page.acf?.book_section.image.id}')`,
              }}
            >
              <div className="bg-black bg-opacity-20 py-20 px-4">
                <div className="flex flex-col max-w-3xl mx-auto items-center justify-center">
                  <h1 className="text-4xl text-center font-tenor-sans text-white">
                    {loaderData.page.acf?.book_section.title}
                  </h1>
                  <div
                    className="text-white mt-4 text-xl text-center"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.book_section.description,
                    }}
                  />
                  <div className="flex items-center justify-center space-x-2">
                    <Link
                      to={
                        bookingDisabled
                          ? getMenuHref(i18n.language, "/coming-soon")
                          : getMenuHref(i18n.language, "/booking")
                      }
                    >
                      <Button variant={"muted"} className="mt-4">
                        {t("Prenota")}
                      </Button>
                    </Link>
                    <Link
                      to={
                        shopDisabled
                          ? getMenuHref(i18n.language, "/coming-soon")
                          : getMenuHref(i18n.language, "/shop")
                      }
                    >
                      <Button variant={"muted"} className="mt-4">
                        {t("Regala")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
